import type { Messenger } from "../messaging/messenger"
import {
  barcodeScanned,
  barcodeScannerConnected,
  barcodeScannerDisconnected,
  type BarcodeScanner,
  type BarcodeScannerDevice,
} from "./barcode-scanner"

export interface KeyboardBarcodeScannerOptions {
  readonly minLength?: number
  readonly maxInterKeyDelayMs?: number
  readonly maxAverageInterKeyDelayMs?: number
  readonly idleTimeoutMs?: number
  readonly terminatorKeys?: readonly string[]
  readonly target?: Window
}

const keyboardDevice: BarcodeScannerDevice = Object.freeze({
  transport: "keyboard",
  productName: "Keyboard scanner detection",
})

export function createKeyboardBarcodeScanner(
  messenger: Messenger,
  options: KeyboardBarcodeScannerOptions = {}
): BarcodeScanner {
  const target = options.target ?? globalThis.window
  const minLength = options.minLength ?? 6
  const maxInterKeyDelayMs = options.maxInterKeyDelayMs ?? 80
  const maxAverageInterKeyDelayMs = options.maxAverageInterKeyDelayMs ?? 30
  const idleTimeoutMs = options.idleTimeoutMs ?? 100
  const terminatorKeys = new Set(options.terminatorKeys ?? ["Enter", "Tab"])
  let connected = false
  let disposed = false
  let value = ""
  let firstKeyAt = 0
  let lastKeyAt = 0
  let idleTimer: ReturnType<typeof setTimeout> | undefined

  const clearCandidate = () => {
    value = ""
    firstKeyAt = 0
    lastKeyAt = 0
    if (idleTimer != null) clearTimeout(idleTimer)
    idleTimer = undefined
  }

  const emitIfScannerLike = () => {
    if (value.length < minLength) {
      clearCandidate()
      return false
    }

    const averageDelay = value.length <= 1 ? 0 : (lastKeyAt - firstKeyAt) / (value.length - 1)
    if (averageDelay > maxAverageInterKeyDelayMs) {
      clearCandidate()
      return false
    }

    const scannedValue = value
    clearCandidate()
    messenger.emit(
      barcodeScanned,
      Object.freeze({
        value: scannedValue,
        device: keyboardDevice,
      })
    )
    return true
  }

  const scheduleIdleDetection = () => {
    if (idleTimer != null) clearTimeout(idleTimer)
    idleTimer = setTimeout(emitIfScannerLike, idleTimeoutMs)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.defaultPrevented || event.repeat || event.isComposing) return

    if (terminatorKeys.has(event.key)) {
      if (emitIfScannerLike()) {
        event.preventDefault()
        event.stopPropagation()
      }
      return
    }

    if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
      clearCandidate()
      return
    }

    const timestamp = event.timeStamp
    if (value.length > 0 && timestamp - lastKeyAt > maxInterKeyDelayMs) clearCandidate()

    if (value.length === 0) firstKeyAt = timestamp
    value += event.key
    lastKeyAt = timestamp
    scheduleIdleDetection()
  }

  async function connect() {
    if (disposed || target == null) return false
    if (connected) return true
    connected = true
    target.addEventListener("keydown", handleKeyDown, true)
    messenger.emit(barcodeScannerConnected, keyboardDevice)
    return true
  }

  async function disconnect() {
    if (!connected) return
    connected = false
    target?.removeEventListener("keydown", handleKeyDown, true)
    clearCandidate()
    messenger.emit(barcodeScannerDisconnected, keyboardDevice)
  }

  return Object.freeze({
    transport: "keyboard" as const,
    supported: target != null,
    get connectedDevice() {
      return connected ? keyboardDevice : null
    },
    connect,
    reconnect: connect,
    disconnect,
    async dispose() {
      if (disposed) return
      disposed = true
      await disconnect()
    },
  })
}
